import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { useGetThemePerformance } from '@terminal/react-query-hooks/themes/useGetThemePerformance';
import { formatNumber } from '@terminal/views/dashboard/calendar-widget/calendar-event-drawer/drawer-content/utils/number-formatter';
import clsx from 'clsx';
import { SimpleHorizon } from '../simple-horizon-selector/SimpleHorizonSelector';
import styles from './PriceReturnPill.module.scss';

/** API uses `1y` for the 12-month return; UI horizon is `12M`. */
const SIMPLE_HORIZON_TO_PERFORMANCE_KEY: Record<
  SimpleHorizon,
  '1d' | '1w' | '1m' | '3m' | '6m' | '1y'
> = {
  '1D': '1d',
  '1W': '1w',
  '1M': '1m',
  '3M': '3m',
  '6M': '6m',
  '12M': '1y',
};

interface PriceReturnPillProps {
  horizon: SimpleHorizon;
  themeId: string;
}

export const PriceReturnPill = ({ horizon, themeId }: PriceReturnPillProps) => {
  const listParams = { ids: themeId };

  const { themes, isFetching } = useGetThemePerformance(listParams);
  const performance = themes?.[0]?.performance;
  const performanceKey = SIMPLE_HORIZON_TO_PERFORMANCE_KEY[horizon];
  const performanceValue = performance?.[performanceKey] ?? undefined;

  if (isFetching) {
    return (
      <SkeletonLoader
        areas={[
          {
            styles: {
              width: '100%',
              height: '20px',
            },
          },
        ]}
      />
    );
  }

  if (performanceValue === undefined || performanceValue === null) {
    return null;
  }

  const value = formatNumber({
    isPercentage: true,
    value: performanceValue,
    addPlus: true,
  });
  const isNegative = performanceValue < 0;

  return (
    <div
      className={clsx(styles.rightHeaderWrapper, {
        [styles.negative]: isNegative,
      })}
    >
      <span className={styles.rightHeaderReturnLabel}>{horizon}</span>
      <span
        className={clsx(styles.rightHeaderReturnValue, {
          [styles.negative]: isNegative,
        })}
      >
        {value}
      </span>
    </div>
  );
};
